<template>
  <app-layout>
    <template #header>
      <div class="flex items-center">
        <div class="flex-1">
          <h1 class="text-lg flex items-center">
            <span
              v-if="state.project.stream && !isSingleStream"
              class="border rounded px-2 py-1 align-center text-xs inline-block leading-none mr-2"
              :style="getStreamColorsByKey(state.project.stream)"
            >
              {{ getStreamNameByKey(state.project.stream) }}
            </span>
            {{ state.project.ref }}
          </h1>
          <p class="text-grey-700 hidden sm:block text-sm">
            {{ state.competition.title }} Form
          </p>
        </div>
        <div class="ml-6 self-center">
          <template
            v-if="
              !state.project.submitted_at &&
                (state.competition.statuses.open || hasExtension)
            "
          >
            <a
              :href="route('home')"
              class="transition-all transition-normal inline-flex items-center px-4 py-3 border-2 border-grey-300 text-grey-800 shadow text-sm font-semibold bg-white hover:bg-gray-50 focus:outline-none focus:shadow-outline no-underline hover:bg-primary-100 hover:border-primary-300 leading-none mr-2"
              @click.prevent="saveApplicationButton"
            >
              Save & Exit
            </a>
            <button
              type="button"
              class="transition-all transition-normal inline-flex items-center px-4 py-3 border-2 border-transparent shadow text-sm font-semibold text-white bg-primary-500 hover:bg-primary-700 hover:text-white leading-none focus:outline-none focus:shadow-outline no-underline ml-2"
              @click="showConfirmation"
            >
              Submit
            </button>
          </template>
          <template v-else>
            <a
              :href="route('home')"
              class="transition-all transition-normal inline-flex items-center px-4 py-3 border-2 border-grey-300 text-grey-800 shadow text-sm font-semibold bg-white hover:bg-gray-50 focus:outline-none focus:shadow-outline no-underline hover:bg-primary-100 hover:border-primary-300 leading-none mr-2"
            >
              Exit to Dashboard
            </a>
          </template>
        </div>
      </div>
    </template>
    <template
      v-if="tabs.length > 1"
      #navigation
    >
      <div class="container -mb-4">
        <div class="flex items-center justify-between border-t mt-5">
          <ul class="list-reset flex">
            <li
              v-for="(tab, index) in tabs"
              :key="index"
              class="mr-8"
            >
              <a
                :href="'#' + tab.hash"
                class="inline-block py-4 border-b-2 -mb-0.5 no-underline"
                :class="{
                  'border-primary-500 text-primary-600 font-semibold':
                    active === index,
                  'border-transparent text-grey-600 hover:text-grey-900 hover:border-grey-300':
                    active !== index
                }"
                @click.stop.prevent="setActive(index)"
              >
                {{ tab.name }}
              </a>
            </li>
          </ul>
        </div>
      </div>
    </template>
    <project-notices :notices="projectNotices" />
    <div class="container">
      <form
        v-if="showApplication && active !== 'feedback'"
        @submit.prevent="() => {}"
      >
        <application-section
          :has-tabs="tabs.length > 1"
          :fieldset="fieldsets[active]"
          :errors="errors"
          :show-errors="showErrors"
          @application:save="saveApplication"
          @application:blur="saveOnBlur"
        />
      </form>
      <div
        v-if="showFeedbackTab && active === 'feedback'"
        class="border-b mb-4"
      >
        <application-presentation-upload
          v-if="state.project.invited_at && !singleStageGrading"
          :project="state.project"
          :user="state.user"
        />
        <component
          :is="`ApplicationFeedback${singleStageGrading ? 'Single' : 'Staged'}`"
          v-for="(assessor, index) in state.project.feedback"
          :key="index"
          :assessor="assessor"
          :class="{
            'border-b mb-12': index + 1 !== state.project.feedback.length
          }"
        />
      </div>
      <ul class="list-reset flex justify-between items-baseline">
        <li class="w-1/3">
          <icon-link
            v-if="active > 0"
            :href="'#' + tabs[active - 1].hash"
            icon="arrow-thin-left-circle"
            @click.stop.prevent="setActive(active - 1)"
          >
            Previous: {{ tabs[active - 1].name }}
          </icon-link>
          <icon-link
            v-if="active === 'feedback'"
            :href="'#' + tabs[tabs.length - 1].hash"
            icon="arrow-thin-left-circle"
            @click.stop.prevent="setActive(tabs.length - 1)"
          >
            Previous: {{ tabs[tabs.length - 1].name }}
          </icon-link>
        </li>
        <li class="w-1/3 text-center">
          <icon-link
            v-if="!reviewMode"
            :href="route('home')"
            class="text-positive-600 hover:text-positive-800"
            @click.prevent="saveApplicationButton"
          >
            Save & Exit
          </icon-link>
          <icon-link
            v-else
            :href="route('home')"
            class="text-positive-600 hover:text-positive-800"
          >
            Dashboard
          </icon-link>
        </li>
        <li class="w-1/3 text-right">
          <icon-link
            v-if="active + 1 < tabs.length"
            :href="'#' + tabs[active + 1].hash"
            icon="arrow-thin-right-circle"
            icon-placement="right"
            @click.stop.prevent="setActive(active + 1)"
          >
            Next: {{ tabs[active + 1].name }}
          </icon-link>
          <div
            v-if="
              active + 1 === tabs.length && !reviewMode && !state.isAssessment
            "
            class="ml-auto w-48"
          >
            <app-button
              type="button"
              @click="showConfirmation"
            >
              Submit
            </app-button>
          </div>
        </li>
      </ul>
    </div>
    <portal to="modals">
      <app-modal
        v-if="confirmationModalVisible"
        class="text-white"
        @close="confirmationModalVisible = false"
      >
        <div class="max-w-md mx-auto content">
          <div v-if="!hasErrors">
            <icon
              icon="important"
              class="icons-knockout w-16 h-16 mb-2"
            />
            <h2 class="mb-1">
              There&rsquo;s no going back...
            </h2>
            <p>
              Please make sure that you have double checked your
              {{ state.competition.title }} before submitting. Once you have
              submitted your {{ state.competition.title }} you will not be able
              to make any changes.
            </p>
            <template v-if="Object.keys(optional_fields).length">
              <p>
                <b>
                  We've also noticed that you havent provided any information
                  for
                  {{
                    Object.keys(optional_fields).length === 1
                      ? "this optional field"
                      : "these optional fields"
                  }}:
                </b>
              </p>
              <ul class="list-reset">
                <li
                  v-for="(value, index) in optional_fields"
                  :key="index"
                  class="mb-0"
                >
                  &mdash; {{ value }}
                </li>
              </ul>
            </template>
            <p>
              If this is correct, please feel free to continue with you
              submission.
            </p>
            <hr>
            <div
              v-if="termsContent"
              v-html="parseMarkdown(termsContent)"
            />
            <h2 class="mb-2">
              Acknowledgement
            </h2>
            <p>By submitting this application you acknowledge and agree:</p>
            <ul>
              <li>
                That the data contained within can be held and processed in
                conjunction with the General Data Protection Regulation
                2016/679;
              </li>
              <li>
                That the information contained is correct to the best of your
                knowledge;
              </li>
              <li>
                That you intend to co-operate with APC & Zenzic with the aim of realising
                the technical developments and formally apply to the
                {{ $config.application_compeition_short_name }}
                programme;
              </li>
              <li>
                You agree to the terms and conditions stated above.
              </li>
            </ul>
            <p>
              All material in this document will be treated as
              <b>STRICTLY CONFIDENTIAL</b>.
            </p>
            <p class="mt-12 flex">
              <app-button
                colour="confirm"
                @click="submitApplication"
              >
                Yes, submit my application
              </app-button>
              <app-button
                colour="close"
                @click="confirmationModalVisible = false"
              >
                Cancel
              </app-button>
            </p>
          </div>
          <div v-else>
            <icon
              icon="important"
              class="icons-negative w-16 h-16 mb-2"
            />
            <h2 class="mb-1">
              We have found some issues with your application.
            </h2>
            <p>
              Before you can submit your application, please make sure that you
              have corrected the following:
            </p>
            <div
              v-for="(section, key, index) in groupedErrors"
              :key="index"
              class="mb-8"
            >
              <h3 class="mb-1">
                {{ key }}
              </h3>
              <ul class="list-reset">
                <li
                  v-for="(error, i) in section"
                  :key="i"
                  class="mb-0"
                >
                  {{ error }}
                </li>
              </ul>
            </div>
            <p class="mt-12">
              <app-button
                colour="confirm"
                @click="confirmationModalVisible = false"
              >
                OK
              </app-button>
            </p>
          </div>
        </div>
      </app-modal>
    </portal>
  </app-layout>
</template>

<script>
import kebabCase from "lodash/kebabCase";
import sortBy from "lodash/sortBy";
import merge from "lodash/merge";
import isEqual from "lodash/isEqual";
import pick from "lodash/pick";
import { format, isBefore, parseISO } from "date-fns";
import { requestMixin } from "../mixins/request";
import { store } from "../store/application";
import ProjectNotices from "../projects/components/ProjectNotices.vue";
import { competitionStreamsMixin } from "../mixins/competitionStreams";
export default {
  name: "ApplicationView",
  components: { ProjectNotices },
  mixins: [requestMixin, competitionStreamsMixin],
  data() {
    return {
      confirmationModalVisible: false,
      active: 0,
      showApplication: false,
      application: store.application,
      errors: store.errors,
      showErrors: store.showErrors
    };
  },
  computed: {
    termsContent() {
      return this.state.terms || "";
    },
    competitionYear() {
      return format(
        parseISO(this.state.competition.application_start_date),
        "yyyy"
      );
    },
    competitionHash() {
      return kebabCase(this.state.competition.title);
    },
    singleStageGrading() {
      return this.state.competition.single_stage_grading === 1;
    },
    showFeedbackTab() {
      return this.singleStageGrading
        ? this.state.project.awarded_at ||
            this.state.project.rejected_at ||
            this.state.project.shortlisted_at
        : this.state.project.feedback;
    },
    /**
     * competitionForm: checks whether it exisits and parses the JSON
     * @returns	{object}			The parsed json, if it exisits
     */
    competitionForm() {
      if(this.state.development_structure) {
        return JSON.parse(this.state.development_structure);
      }
      if (!this.state.competition.application_template) return [];
      return JSON.parse(this.state.competition.application_template);
    },
    /**
     * fieldsets: checks that the application form has fieldsets
     * @returns	{array}			Returns and array of fieldsets
     */
    fieldsets() {
      if (!this.competitionForm.fieldset) return [];
      return this.competitionForm.fieldset;
    },
    /**
     * tabs: used to create usable data for each of the tabs
     * @returns	{array}		contains each of the fieldset's name and hash
     */
    tabs() {
      if (!this.fieldsets) return [];
      return this.fieldsets.map(item =>
        Object.assign(
          {},
          {
            name: item.legend,
            hash: this.hashFriendly(item.legend)
          }
        )
      );
    },
    hasErrors() {
      return Object.keys(this.errors).length > 0;
    },
    groupedErrors() {
      const keys = Object.keys(this.errors).map(i => i.split("."));
      const fieldInput = keys.reduce((obj, item) => {
        let field = this.getFieldset(item[0]);
        if (field.legend in obj === false) {
          obj[field.legend] = [];
        }
        if (obj[field.legend].indexOf(field.question) === -1) {
          obj[field.legend].push(field.question);
        }
        return obj;
      }, {});
      return fieldInput;
    },
    reviewMode() {
      return store.reviewMode;
    },
    now() {
      return new Date();
    },
    hasPassed() {
      if (this.state.project.application_extended_until !== null) {
        return isBefore(this.application_extended_until, this.now);
      }
      return !this.state.competition.statuses.open;
    },
    application_end_date() {
      return parseISO(this.state.competition.application_end_date);
    },
    application_extended_until() {
      if (!this.state.project.application_extended_until) return;
      return parseISO(this.state.project.application_extended_until);
    },
    hasExtension() {
      return !!this.application_extended_until && !this.hasPassed;
    },
    optional_fields() {
      const fields = this.fieldsets.reduce((arr, field) => {
        arr = [...arr, ...field.fields];
        return arr;
      }, []);
      return fields.reduce((obj, input) => {
        const optional = input.sub_fields.reduce((arr, f) => {
          if (f.type === "break" || f.type === "categories") return arr;
          if (!f.attributes.required) {
            if (
              !this.application[input.key][f.key] ||
              !this.application[input.key][f.key].length
            ) {
              arr.push(f.label);
            }
          }
          return arr;
        }, []);

        if (
          input.repeatable &&
          input.repeatable.min === 0 &&
          !this.application[input.key].length
        ) {
          obj[input.key] = input.label;
        }

        if (optional.length) {
          obj[input.key] = optional.join(", ");
        }
        return obj;
      }, {});
    },
    projectNotices() {
      const notice = [];
      if (this.hasExtension && !this.state.project.submitted_at) {
        const extensionDate = this.format(
          this.application_extended_until,
          "do MMMM yyyy hh:mm"
        );
        notice.push({
          type: "neutral",
          content: `Your application has been extended until ${extensionDate}`
        });
      } else if (
        !this.state.project.submitted_at &&
        !this.state.competition.statuses.open
      ) {
        notice.push({
          type: "neutral",
          content: `${this.state.competition.title} is closed for submission`
        });
      } else if (this.state.project.rejected_at) {
        notice.push({
          type: "negative",
          content: `Your ${this.state.competition.title} was unsuccessful`
        });
      } else if (this.state.project.invited_at) {
        notice.push({
          type: "positive",
          content: `Your ${this.state.competition.title} was successful`
        });
      } else if (this.state.project.submitted_at) {
        notice.push({
          type: "neutral",
          content: `Your ${this.state.competition.title} is under review`
        });
      }
      return notice;
    }
  },
  watch: {
    active: function() {
      return this.saveApplication();
    }
  },
  created() {
    document.title = `${this.state.project.ref} Application Form :: ${document.title}`;

    const checkChanges = e => this.checkChanges(e);

    window._temp = this.state.project.application;
    window._project = this.state.project.uuid;
    store.universities = this.state?.universities || [];

    window.addEventListener("turbolinks:before-visit", checkChanges);
    this.$once("hook:beforeDestroy", () => {
      delete window._temp;
      delete window._project;
      store.reviewMode = false;
      store.showErrors = false;
      store.application = {};
      store.errors = {};
      store.universities = [];
      window.removeEventListener("turbolinks:before-visit", checkChanges);
    });

    if (
      this.state.project.submitted_at ||
      this.state.isAssessment ||
      this.hasPassed
    ) {
      store.reviewMode = true;
    }

    if (this.competitionForm) {
      const fields = sortBy(this.competitionForm.fieldset, ["order"]);
      this.setApplication(fields);
      this.$once("hook:mounted", () => {
        this.showApplication = true;
      });
    }

    if (this.application && window._temp) {
      if (
        this.application.hasOwnProperty("project_partners") &&
        window._temp.hasOwnProperty("project_partners")
      ) {
        this.application.project_partners = [...window._temp.project_partners];
      }
    }

    this.loadTabWithHash(window.location.hash);
  },
  methods: {
    loadTabWithHash(hash) {
      if (!hash) return;
      hash = hash.replace("#", "");
      const index =
        hash === "feedback"
          ? "feedback"
          : this.tabs.findIndex(tab => tab.hash === hash);
      if (index < 0) return;
      return this.setActive(index);
    },
    getFieldset(key) {
      const groups = this.competitionForm.fieldset.map(item => {
        const errorField = item.fields.find(field => field.key === key);
        if (errorField)
          return { legend: item.legend, question: errorField.label };
      });
      const group = groups.filter(Boolean);
      return group[0];
    },
    /**
     * hashFriendly: creates a url friendly string of the value
     * @param		{string}	value	the value that will be "Kebabbed"
     * @return	{string}				the kebabbed string e.g. "this-is-kebabbed"
     */
    hashFriendly(value) {
      return kebabCase(value);
    },
    /**
     * setActive: used to set the the active fieldset of the application
     * @param	{int}	index	the index of the fieldset
     */
    setActive(index) {
      window.location.hash = this.tabs[index] ? this.tabs[index].hash : index;
      this.scrollTop();
      this.saveApplication;
      this.active = index;
    },
    /**
     * setApplication: this is used to set the initial values of the
     * application on load. This will then merge the users saved application
     * to the result.
     * @param {object}	application	The application form object
     * @return											Merge the empty application with the users application
     */
    setApplication(application) {
      // set initial defaults

      // Get all the application forms fields
      application.map(({ fields }) =>
        // loop over all the subfields and set the parent key
        fields.map(field => {
          /**
           * check for repeatable field
           * TODO: look at creating seperate methods for each and reduce side-effects
           */
          if (!field.hasOwnProperty("repeatable")) {
            this.$set(this.application, field.key, {});
            return this.setApplicationFieldKeyVal(field);
          } else {
            this.$set(this.application, field.key, []);
            return this.setApplicationFieldArray(field);
          }
        })
      );

      // merge with saved application
      return merge(this.application, window._temp);
    },
    checkDefault(defaultValue) {
      if (typeof defaultValue.match !== "function") return defaultValue;
      if (!defaultValue.match(/\[[A-Z]+\]/g)) return defaultValue;
      return this.matchedValue(defaultValue);
    },
    matchedValue(defaultValue) {
      const value = defaultValue.replace(/[\s\W]+/g, "").toLowerCase();
      const key = Object.freeze({
        company: {
          company_number: this.state.user.company.number,
          company_details: pick(this.state.user.company, [
            "id",
            "name",
            "address_line_1",
            "address_line_2",
            "town",
            "county",
            "postcode",
            "companies_house_info"
          ])
        },
        name: this.state.user.name,
        email: this.state.user.email,
        telephone: this.state.user.telephone || "",
        jobtitle: this.state.user.job_title || ""
      });
      return key[value];
    },
    /**
     * setApplicationFieldKeyVal: if the form section is not multiple/repeatable,
     * then do a straight set.
     * @param	{object}	field	the individual field for the current fieldgroup
     * @return								Side-effect of updating the the user's application with the default attributes defined on the form.
     */
    setApplicationFieldKeyVal(field) {
      return field.sub_fields.map(({ key, type, attributes }) => {
        if (type === "break" || type === "categories") return;
        return this.$set(
          this.application[field.key],
          key,
          this.checkDefault(attributes.default)
        );
      });
    },
    /**
     * setApplicationFieldKeyArray: Similar to setApplicationFieldKeyVal, the method maps each of the keys to a temp object before setting the minimum amount to the array.
     * @param	{object}	field	the individual field for the current fieldgroup
     * @return								Side-effect of updating the the user's application with the default attributes defined on the form the minimum number of entries required.
     */
    setApplicationFieldArray(field) {
      const temp = {};
      field.sub_fields.map(({ key, attributes }) => {
        return (temp[key] = attributes.default);
      });

      while (this.application[field.key].length < field.repeatable.min) {
        this.application[field.key].push({ ...temp });
      }
    },
    /**
     * saveApplicationButton: Used to handle the save event of the 'Save & Exit' buttons
     * @param	{object}	e	the event data that is passed.
     * @return						side effect of fireing the saveApplication method and firing the click
     */
    saveApplicationButton(e) {
      if (this.reviewMode && this.state.isAssessment) return;
      const returnLink =
        e.target.getAttribute("href") ||
        e.target.parentElement.getAttribute("href");
      window._temp = this.application;
      this.saveApplication(() => Turbolinks.visit(returnLink));
    },
    /**
     * saveApplication: Save the application
     * @param		{function}	cb	a callback function that is fired on a successful request.
     * @return	{function}		the callback that was assigned with the response.
     */
    saveApplication(cb = function() {}) {
      // Dont save on review mode
      if (this.reviewMode) return;
      return axios
        .put(route("applications.process", this.state.project.uuid), {
          application: { ...this.application }
        })
        .then(response => {
          const errors = response.data.errors || {};
          this.errors = errors;
          return cb(response);
        })
        .catch(error => this.requestError(error));
    },
    /**
     * saveOnBlue: used to just fire a quiet save on blur
     * @return	{function}	fires the save application mathod
     */
    saveOnBlur() {
      if (isEqual(this.application, window._temp)) return;
      return this.saveApplication();
    },
    /**
     * checkChanges: make sure that the form has not changed
     * @param		{function}	event	the event object
     * @return	{function}				the save application method
     */
    checkChanges(event) {
      if (!isEqual(this.application, window._temp)) {
        if (confirm("Would you like to save you application before leaving?")) {
          event.preventDefault();
          this.saveApplication(() => {
            window._temp = this.application;
            Turbolinks.visit(event.data.url);
          });
        }
      }
    },
    showConfirmation() {
      return this.saveApplication(() => {
        this.confirmationModalVisible = true;
        this.showErrors = true;
      });
    },
    submitApplication() {
      if (this.reviewMode) return;
      window._temp = this.application;
      return axios
        .put(
          route("applications.process", this.state.project.uuid),
          {
            application: { ...this.application }
          },
          {
            headers: {
              "X-APC-Save-Application": true
            }
          }
        )
        .then(() => {
          return Turbolinks.visit("/");
        })
        .catch(error => this.requestError(error));
    }
  }
};
</script>
